import { useCallback, useEffect, useRef } from 'react';
import { DemeterLeadingIndicatorModel } from '../../../Generated/Raven-Demeter';
import { useApplicationSelector } from '../../../Redux/ReduxStore';
import { selectStoreId } from '../../../Redux/Slices/SystemSlice';
import lightstreamerMarketPricesService from '../../Services/MarketPrices/LightstreamerMarketPricesService';

export interface IndicatorsWidgetInfoInterface {
    title: string;
    change: number;
    currency: string;
    value?: number | null;
    asOfDate: string | null | undefined;
    latestPriceAsOfDate: string | null | undefined;
    settlementPriceAsOfDate: string | null | undefined;
    reutersInstrumentCode: string | null | undefined;
    commodity?: any;
    region?: any;
    isFuturesSettlement: boolean;
    indicatorModel: DemeterLeadingIndicatorModel;
    displayDecimalPlacesMinimum: number;
    displayDecimalPlacesMaximum: number;
}

interface UseLightstreamerMarketPricesReturn {
    startSubscription: (reutersInstrumentCodes: string[]) => void;
    stopSubscription: () => void;
    updateSubscription: (reutersInstrumentCodes: string[]) => void;
}

const useLightstreamerMarketPrices = (): UseLightstreamerMarketPricesReturn => {
    const storeId = useApplicationSelector(selectStoreId);
    const currentCodesReference = useRef<string[]>([]);
    const isSubscriptionActiveReference = useRef<boolean>(false);

    const startSubscription = useCallback(
        (reutersInstrumentCodes: string[]) => {
            const filteredCodes = reutersInstrumentCodes.filter((code) => code != null);

            if (filteredCodes.length === 0) {
                return;
            }

            currentCodesReference.current = filteredCodes;
            isSubscriptionActiveReference.current = true;
            lightstreamerMarketPricesService.start(storeId, filteredCodes);
        },
        [storeId],
    );

    const stopSubscription = useCallback(() => {
        if (isSubscriptionActiveReference.current) {
            currentCodesReference.current = [];
            isSubscriptionActiveReference.current = false;
            lightstreamerMarketPricesService.stop(storeId);
        }
    }, [storeId]);

    const updateSubscription = useCallback(
        (reutersInstrumentCodes: string[]) => {
            const filteredCodes = reutersInstrumentCodes.filter((code) => code != null);
            const codesChanged =
                filteredCodes.length !== currentCodesReference.current.length || !filteredCodes.every((code) => currentCodesReference.current.includes(code));

            if (codesChanged) {
                currentCodesReference.current = filteredCodes;

                if (filteredCodes.length > 0) {
                    isSubscriptionActiveReference.current = true;
                    lightstreamerMarketPricesService.start(storeId, filteredCodes);
                } else {
                    isSubscriptionActiveReference.current = false;
                    lightstreamerMarketPricesService.stop(storeId);
                }
            }
        },
        [storeId],
    );

    useEffect(
        () => () => {
            if (isSubscriptionActiveReference.current) {
                lightstreamerMarketPricesService.stop(storeId);
                isSubscriptionActiveReference.current = false;
            }
        },
        [storeId],
    );

    return {
        startSubscription,
        stopSubscription,
        updateSubscription,
    };
};

export default useLightstreamerMarketPrices;
